import { useState, useEffect } from 'react';
import StockTable from './StockTable';
import { Stock } from '../data/mockData';
import { useData } from '../context/DataContext';

export default function ONeil() {
  const [activeTab, setActiveTab] = useState<'pocket' | 'breakout' | 'rps'>('pocket');
  const [selectedRPS, setSelectedRPS] = useState<number[]>([10, 20, 50, 120, 250]);
  
  const { stocks, signals, isLoading } = useData();
  const [pocketPivotStocks, setPocketPivotStocks] = useState<Stock[]>([]);
  const [newHighStocks, setNewHighStocks] = useState<Stock[]>([]);

  useEffect(() => {
    if (stocks.length > 0) {
      // Filter for Pocket Pivot
      const pocketPivotCodes = new Set(signals
        .filter(s => s.signalType === '口袋支点' || s.signalType === 'pocket_pivot')
        .map(s => s.stock.code));
      setPocketPivotStocks(stocks.filter(s => pocketPivotCodes.has(s.code)));

      // Filter for New High
      const newHighCodes = new Set(signals
        .filter(s => s.signalType === '一年新高' || s.signalType === 'new_high')
        .map(s => s.stock.code));
      setNewHighStocks(stocks.filter(s => newHighCodes.has(s.code)));
    }
  }, [stocks, signals]);


  const tabs = [
    { id: 'pocket' as const, label: '口袋支点' },
    { id: 'breakout' as const, label: '新高突破' },
    { id: 'rps' as const, label: 'RPS' }
  ];

  const rpsFilters = [
    { value: 10, label: 'RPS10' },
    { value: 20, label: 'RPS20' },
    { value: 50, label: 'RPS50' },
    { value: 120, label: 'RPS120' },
    { value: 250, label: 'RPS250' }
  ];

  const toggleRPSFilter = (value: number) => {
    if (selectedRPS.includes(value)) {
      if (selectedRPS.length > 1) {
        setSelectedRPS(selectedRPS.filter(v => v !== value));
      }
    } else {
      setSelectedRPS([...selectedRPS, value].sort((a, b) => a - b));
    }
  };

  const getRPSColumns = () => {
    const baseColumns = [
      {
        key: 'code' as const,
        label: '代码'
      },
      {
        key: 'name' as const,
        label: '名称',
        render: (_value: string, stock: Stock) => (
          <div className="flex items-center gap-1.5">
            <span>{stock.name}</span>
            {stock.topic && (
              <span className="text-xs px-1.5 py-0.5 bg-[#1a56db]/10 text-[#1a56db] rounded">
                {stock.topic}
              </span>
            )}
          </div>
        )
      },
      {
        key: 'changePercent' as const,
        label: '涨跌幅',
        render: (value: number) => (
          <span className={value >= 0 ? 'text-[#dc3545]' : 'text-[#28a745]'}>
            {value >= 0 ? '+' : ''}{value?.toFixed(2)}%
          </span>
        )
      },
      {
        key: 'signalDate' as const,
        label: '信号日',
        render: (value: string) => <span className="text-muted-foreground">{value || '-'}</span>
      },
      {
        key: 'dataDate' as const,
        label: '数据日',
        render: (value: string) => <span className="text-muted-foreground">{value || '-'}</span>
      }
    ];

    const rpsColumns = selectedRPS.map(rps => ({
      key: `rps${rps}` as keyof Stock,
      label: `RPS${rps}`,
      render: (value: number) => {
        let colorClass = 'text-[#28a745]';
        if (value >= 90) colorClass = 'text-[#dc3545] font-semibold';
        else if (value >= 80) colorClass = 'text-[#f0ad4e]';
        else if (value >= 70) colorClass = 'text-[#5cb85c]';
        
        return <span className={colorClass}>{value || '-'}</span>;
      }
    }));

    // Insert RPS columns before 'signalDate'
    const insertIndex = 3;
    return [
      ...baseColumns.slice(0, insertIndex),
      ...rpsColumns,
      ...baseColumns.slice(insertIndex)
    ];
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin w-6 h-6 border-2 border-[#1a56db] border-t-transparent rounded-full" />
          <span>加载数据中...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="px-4 pt-4 pb-2">
        <h1 className="text-2xl font-semibold mb-4 text-foreground">欧奈尔</h1>
        
        {/* Tabs */}
        <div className="flex border-b border-border mb-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 pb-3 text-sm font-medium transition-colors relative ${
                activeTab === tab.id 
                  ? 'text-[#1a56db]' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1a56db]" />
              )}
            </button>
          ))}
        </div>

        {/* Filters (only for RPS tab) */}
        {activeTab === 'rps' && (
          <div className="flex flex-wrap gap-2 mb-4">
            {rpsFilters.map(filter => (
              <button
                key={filter.value}
                onClick={() => toggleRPSFilter(filter.value)}
                className={`px-3 py-1 rounded-full text-xs transition-colors ${
                  selectedRPS.includes(filter.value)
                    ? 'bg-[#1a56db] text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden bg-background">
        {activeTab === 'rps' && (
          <StockTable 
            stocks={stocks} 
            columns={getRPSColumns()} 
            defaultSort={{ key: 'changePercent', direction: 'desc' }}
            pageSize={20}
          />
        )}
        
        {activeTab === 'pocket' && (
          <StockTable 
            stocks={pocketPivotStocks} 
            columns={getRPSColumns()} 
            defaultSort={{ key: 'changePercent', direction: 'desc' }}
            pageSize={20}
          />
        )}
        
        {activeTab === 'breakout' && (
          <StockTable 
            stocks={newHighStocks} 
            columns={getRPSColumns()} 
            defaultSort={{ key: 'changePercent', direction: 'desc' }}
            pageSize={20}
          />
        )}
      </div>
    </div>
  );
}